import findLast from 'lodash/findLast'
import findLastIndex from 'lodash/findLastIndex'

import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'

import {
  isWeekdayScopedInterval,
  resolveOperatingIntervalIsDaily,
} from './operatingIntervalMode'

// A resource's hours of operation allow at most ONE daily window: saving a daily
// window supersedes the existing one instead of adding a second, and it is ordered
// first so it heads the chips. Date windows are unconstrained. While a resource has
// no daily window of its own it falls back to the project working hours, which is
// what the inherited-hours chip displays and what validation applies.
//
// This module is the single source of that rule, shared by the per-resource cell,
// the bulk editor, the save payloads and the booking validator. Records stored before
// the rule can still hold several daily windows, so read paths collapse rather than
// assume; every write path now goes through here, so no new ones are created.

// The daily window in effect for a resource, or undefined when it has none. The most
// recently added one wins: there is no timestamp on an interval, and new windows are
// appended.
export function findDailyOperatingInterval(
  intervals: readonly IMsDateInterval[],
): IMsDateInterval | undefined {
  return findLast(intervals, resolveOperatingIntervalIsDaily)
}

// Keeps the window at `keptIndex` as the only daily one and moves it to the front,
// so the window that applies every day heads the list; date windows follow in their
// existing order. A negative index means there is no daily window to keep.
function withDailyFirst(
  intervals: readonly IMsDateInterval[],
  keptIndex: number,
): IMsDateInterval[] {
  const dated = intervals.filter(
    (interval, index) =>
      index !== keptIndex && !resolveOperatingIntervalIsDaily(interval),
  )
  return keptIndex < 0 ? dated : [intervals[keptIndex], ...dated]
}

// Drops every daily window but the effective one, and orders it first. A resource
// on a weekly schedule keeps one window per weekday instead: the two recurrence
// modes are mutually exclusive, which the server enforces as well, so the weekday
// windows win and any every-day window is dropped rather than layered under them.
export function withSingleDailyInterval(
  intervals: readonly IMsDateInterval[],
): IMsDateInterval[] {
  const weekdayScoped = intervals.filter(isWeekdayScopedInterval)

  if (weekdayScoped.length) {
    // The most recently added window for a weekday wins, as it does for the
    // every-day window below: intervals carry no timestamp and new ones append.
    const byWeekday = new Map<number, IMsDateInterval>()
    weekdayScoped.forEach(interval =>
      byWeekday.set(interval.dayOfWeek, interval),
    )

    const dated = intervals.filter(
      interval => !resolveOperatingIntervalIsDaily(interval),
    )

    return [
      ...Array.from(byWeekday.values()).sort(
        (a, b) => a.dayOfWeek - b.dayOfWeek,
      ),
      ...dated,
    ]
  }

  return withDailyFirst(
    intervals,
    findLastIndex(intervals, resolveOperatingIntervalIsDaily),
  )
}

// What the editor opens on: the interval being edited, or — when adding — the
// resource's existing daily window, so the Daily tab edits that single window in
// place instead of silently evicting it on save. The Date tab still appends a new
// date window. Undefined when there is nothing to seed from.
export function resolveOperatingEditorSeed(
  intervals: IMsDateInterval[],
  editIndex: number | null,
): IMsDateInterval | undefined {
  const editing = editIndex == null ? undefined : intervals[editIndex]
  return editing ?? findDailyOperatingInterval(intervals)
}

// Merges an interval the user just saved into the list, keeping the single-daily
// rule. `editIndex` is null when adding: a saved daily window then targets the
// existing daily window (so it replaces rather than appends), while a saved date
// window always appends.
//
// Editing replaces the window it was opened on only while the recurrence mode
// holds. Switching the editor's tab is navigation, not conversion: the two modes
// are separate layers that coexist — a dated window overrides the recurring one
// on its own days — so leaving one tab for the other must not delete what the
// first was showing. Opening a date chip and saving an every-day window used to
// take the date with it, and the Daily tab never displayed that window to begin
// with, so nothing on screen said it was at stake. The saved window then lands
// where its own mode belongs: a daily one over the existing daily window, a
// dated one appended.
export function upsertOperatingInterval(
  intervals: IMsDateInterval[],
  interval: IMsDateInterval,
  editIndex: number | null,
): IMsDateInterval[] {
  const isDaily = resolveOperatingIntervalIsDaily(interval)
  // An index that no longer exists (the list changed while the editor was open) is
  // treated as adding, so the saved interval is never dropped on the floor. The
  // bulk editor's own "adding" sentinel is -1, hence the lower bound too.
  const isEditingExisting =
    editIndex != null &&
    editIndex >= 0 &&
    editIndex < intervals.length &&
    resolveOperatingIntervalIsDaily(intervals[editIndex]) === isDaily
  const targetIndex = isEditingExisting
    ? editIndex
    : isDaily
      ? findLastIndex(intervals, resolveOperatingIntervalIsDaily)
      : -1

  const next =
    targetIndex < 0
      ? [...intervals, interval]
      : intervals.map((existing, index) =>
          index === targetIndex ? interval : existing,
        )
  if (!isDaily) return next

  // Turning an existing date window into a daily one — or editing legacy data that
  // already had several — can leave other daily windows behind. The one just saved
  // is the one that survives, so this can't collapse onto a different index.
  const savedIndex = targetIndex < 0 ? next.length - 1 : targetIndex
  return withDailyFirst(next, savedIndex)
}
